// react
import { MouseEvent, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Box,
	Checkbox,
	Divider,
	FormControlLabel,
	IconButton,
	Popover,
	Tooltip,
	Typography,
} from '@mui/material';

// icons
import {
	ViewColumnOutlined as IconViewColumn,
	RestartAltOutlined as IconReset,
} from '@mui/icons-material';

// types
import { TableHeader } from '../Table/Table';

// theme
import { theme } from '../../../theme';

// props
export interface ColumnConfigProps {
	allHeaders: { header: TableHeader; visible: boolean }[];
	isCustomised: boolean;
	onReset: () => void;
	onToggle: (value: string) => void;
}

export const ColumnConfig = ({
	allHeaders,
	isCustomised,
	onReset,
	onToggle,
}: ColumnConfigProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
	const open = Boolean(anchor);

	return (
		<>
			<Tooltip title={t('tableColumns', 'Configure columns')}>
				<IconButton
					onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget)}
					size="small"
					sx={{
						color: isCustomised ? theme.palette.brand.blue[600] : theme.palette.brand.grey[500],
					}}>
					<IconViewColumn fontSize="small" />
				</IconButton>
			</Tooltip>

			<Popover
				anchorEl={anchor}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				onClose={() => setAnchor(null)}
				open={open}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				slotProps={{
					paper: {
						sx: {
							minWidth: 220,
							maxHeight: 400,
							overflowY: 'auto',
							padding: theme.spacing(1),
						},
					},
				}}>
				<Box sx={{ padding: theme.spacing(0.5, 1) }}>
					<Typography fontWeight={600} variant="caption" color="brand.grey.700">
						{t('tableColumnsTitle', 'Show / Hide Columns')}
					</Typography>
				</Box>

				<Divider sx={{ my: 0.5 }} />

				{allHeaders.map(({ header, visible }) => (
					<FormControlLabel
						key={header.value}
						control={
							<Checkbox
								checked={visible}
								onChange={() => onToggle(header.value)}
								size="small"
								sx={{ py: 0.25 }}
							/>
						}
						label={
							<Typography variant="body2" color="brand.grey.800">
								{header.label || header.value}
							</Typography>
						}
						sx={{ display: 'flex', mx: 0, px: 1, py: 0.25, borderRadius: 1 }}
					/>
				))}

				{isCustomised && (
					<>
						<Divider sx={{ my: 0.5 }} />
						<Box
							component="button"
							onClick={() => {
								onReset();
							}}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
								width: '100%',
								padding: theme.spacing(0.75, 1),
								borderRadius: 1,
								color: theme.palette.brand.blue[600],
								'&:hover': {
									backgroundColor: theme.palette.brand.blue[100],
								},
							}}>
							<IconReset fontSize="small" />
							<Typography variant="caption" fontWeight={600}>
								{t('tableColumnsReset', 'Reset to defaults')}
							</Typography>
						</Box>
					</>
				)}
			</Popover>
		</>
	);
};
